import clsx from 'clsx';
import { type CSSProperties, useEffect, useId, useRef, useState } from 'react';

type Params = {
  autoHideDuration: number;
  onTimeout: () => void;
};

type TutorToastView = {
  className: string;
  onBlur: () => void;
  onFocus: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  style: CSSProperties;
  textId: string;
};

/** The bar reads the pause from the `--paused` class, so the line and the time stop together. */
function useTutorToast({ autoHideDuration, onTimeout }: Params): TutorToastView {
  const textId = useId();
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const remainingRef = useRef<number>(autoHideDuration);
  const isPaused = isHovered || isFocused;

  useEffect(() => {
    if (isPaused || remainingRef.current <= 0) {
      return undefined;
    }

    const startedAt = Date.now();
    const timeoutId = setTimeout(() => {
      remainingRef.current = 0;
      onTimeout();
    }, remainingRef.current);

    // Each cleanup takes off the time that ran, so a pause never restarts the count.
    return () => {
      clearTimeout(timeoutId);
      remainingRef.current -= Date.now() - startedAt;
    };
  }, [isPaused, onTimeout]);

  function onMouseEnter(): void {
    setIsHovered(true);
  }

  function onMouseLeave(): void {
    setIsHovered(false);
  }

  function onFocus(): void {
    setIsFocused(true);
  }

  function onBlur(): void {
    setIsFocused(false);
  }

  return {
    className: clsx('tutor-toast', { 'tutor-toast--paused': isPaused }),
    onBlur,
    onFocus,
    onMouseEnter,
    onMouseLeave,
    style: { '--tutor-toast-duration': `${autoHideDuration}ms` } as CSSProperties,
    textId,
  };
}

export default useTutorToast;
